import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import moment from 'moment';
import { ExportToCsv } from 'export-to-csv';
import request from '../../../services/Request';

const LIMIT = 20;

const money = (n, currency) =>
  `${currency === 'CAD' ? 'CA$' : '$'}${Number(n || 0).toFixed(2)}`;

// The order history a member cares about: what they bought, where, for how much.
export default function MemberOrderPanel({ member }) {
  const theme = useTheme();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await request({
          method: 'post',
          url: '/clientUser/orders',
          data: { _id: member._id, number: LIMIT },
        });
        if (!cancelled) setOrders(res.data || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error?.message || 'Could not load orders');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [member._id]);

  const productNames = (order) =>
    (order.products || [])
      .filter((p) => !p.err)
      .map((p) => p.productInfo?.name || p.product?.name)
      .filter(Boolean)
      .join(', ');

  const handleDownload = () => {
    const rows = orders.map((order) => ({
      'Order ID': order.id || '',
      Date: moment(order.createdAt).format('MM/DD/YYYY HH:mm'),
      Machine: order.machineInfo?.name || '',
      Products: productNames(order),
      Items: (order.products || []).filter((p) => !p.err).length,
      Tax: Number(order.tax || 0).toFixed(2),
      Total: Number(order.total || 0).toFixed(2),
      Currency: order.currency || 'USD',
      Status: order.status || '',
    }));

    new ExportToCsv({
      filename: `Orders_${member.username || member.email || member._id}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useBom: true,
      useKeysAsHeaders: true,
    }).generateCsv(rows);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 3 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Loading orders...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2.5,
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {member.username} — last {Math.min(orders.length, LIMIT)} order(s)
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            disabled={orders.length === 0}
            onClick={handleDownload}
          >
            Download CSV
          </Button>
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={() => window.open(`/dashboard/memberorder/${member._id}`, '_blank')}
          >
            Full history
          </Button>
        </Box>
      </Box>

      {orders.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          This member has no orders yet.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Order ID</TableCell>
                <TableCell>Machine</TableCell>
                <TableCell>Products</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order._id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {moment(order.createdAt).format('MM/DD/YYYY HH:mm')}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {order.id}
                  </TableCell>
                  <TableCell>{order.machineInfo?.name || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {productNames(order) || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {money(order.total, order.currency)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={order.status || '-'}
                      color={order.status === 'success' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
